import { isRecord, finiteNumber } from "../service-json";
import type { CameraDevice, CameraFacing, ZoomRange } from "../camera-lens";

/**
 * Ce qu'une caméra déclare d'elle-même au service de séance.
 *
 * # Pourquoi l'appareil doit le dire, plutôt que le studio le deviner
 *
 * Parce qu'il rogne avant son encodeur : de lui, on ne reçoit que la découpe.
 * Le studio remontait jusqu'ici les dimensions de la caméra depuis l'image
 * publiée (`sourceFromOutput`), en la divisant par le zoom demandé. Ça marchait
 * tant que la définition publiée suivait la découpe — plus dès qu'elle est
 * plafonnée, car le plafond détruit précisément l'information qu'on remontait.
 * Or le studio a besoin de ces dimensions pour savoir de combien un geste peut
 * déplacer le cadre.
 *
 * Et les objectifs d'un appareil ne se devinent pas du tout : seul son
 * navigateur les liste.
 *
 * # Ce que ça n'est pas
 *
 * Une source d'autorité. Ces valeurs sont **déclarées par l'appareil**, jamais
 * signées : elles disent ce qu'il prétend avoir. On s'en sert pour proposer un
 * objectif et pour calculer un cadrage — jamais pour décider d'un droit. Ce qui
 * décide d'un droit est dans le jeton, signé par Symfony
 * (`lib/live/live-attributes.ts`).
 */
export interface CameraCapabilities {
  /** Ce que la caméra capte en ce moment, en pixels. */
  readonly width: number;
  readonly height: number;
  /** Les objectifs du côté filmé, dans l'ordre où le navigateur les donne. */
  readonly lenses: readonly DeclaredLens[];
  /** L'objectif ouvert, parmi {@link CameraCapabilities.lenses} ; vide si inconnu. */
  readonly lens: string;
  /** L'étendue du zoom de la piste ; `null` quand elle ne sait pas zoomer. */
  readonly zoom: { readonly min: number; readonly max: number } | null;
}

/** Un objectif déclaré : l'identifiant que `getUserMedia` accepte, et son nom. */
export interface DeclaredLens {
  readonly id: string;
  readonly label: string;
}

/** Les caméras d'une séance qui se sont déclarées, par identité LiveKit. */
export type DeclaredCameras = Readonly<Record<string, CameraCapabilities>>;

/**
 * Ce que cette caméra a à déclarer, lu sur la piste ouverte et sur la liste du
 * navigateur.
 *
 * Les objectifs du côté filmé seulement : proposer au studio d'ouvrir la
 * caméra avant quand le téléphone est posé face au clavier n'aurait aucun sens.
 * Un appareil dont le navigateur ne dit pas le côté reste dans la liste — mieux
 * vaut un objectif de trop qu'un grand angle introuvable, la même règle que
 * `lensOffer`.
 */
export function readCapabilities(
  track: MediaStreamTrack,
  cameras: readonly CameraDevice[],
  side: CameraFacing,
  zoom: ZoomRange | undefined,
): CameraCapabilities {
  const settings = track.getSettings();
  const active = settings.deviceId ?? "";

  return {
    width: settings.width ?? 0,
    height: settings.height ?? 0,
    lens: active,
    lenses: cameras
      .filter((camera) => camera.deviceId === active || camera.facing.length === 0 || camera.facing.includes(side))
      .map((camera) => ({ id: camera.deviceId, label: camera.label })),
    zoom:
      typeof zoom?.min === "number" && typeof zoom.max === "number" && zoom.min > 0
        ? { min: zoom.min, max: zoom.max }
        : null,
  };
}

/**
 * Déclare ces capacités au service, avec le jeton de l'appareil.
 *
 * L'identité ne part pas : le service la lit dans le jeton, si bien qu'une
 * caméra ne déclare que pour elle-même.
 *
 * Même discipline d'échec que le reste du service : `false` sans lever, motif
 * dans la console. Un appareil qui n'a pas pu se déclarer filme quand même —
 * le studio cadrera sans connaître ses objectifs, comme avant qu'ils existent.
 */
export async function declareCapabilities(
  clockUrl: string,
  room: string,
  token: string,
  caps: CameraCapabilities,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  if (clockUrl === "" || caps.width <= 0 || caps.height <= 0) {
    return false;
  }

  try {
    const response = await fetchImpl(`${clockUrl}/sessions/${room}/cameras/capabilities`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        width: Math.round(caps.width),
        height: Math.round(caps.height),
        lens: caps.lens,
        lenses: caps.lenses,
        ...(caps.zoom === null ? {} : { zoom: caps.zoom }),
      }),
    });

    if (!response.ok) {
      console.warn("[live] capacités de caméra refusées :", response.status);
    }

    return response.ok;
  } catch (cause) {
    console.warn("[live] capacités de caméra non déclarées :", cause);

    return false;
  }
}

/**
 * Lit ce que les caméras de la séance ont déclaré. Réservé par le service à qui
 * pilote.
 *
 * Une table vide quand rien ne répond : le studio cadre alors sans dimensions
 * connues, comme avant que la route existe. C'est aussi ce qu'il trouve après
 * un redémarrage du service, le temps que les appareils se redéclarent.
 */
export async function fetchDeclaredCameras(
  clockUrl: string,
  room: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DeclaredCameras> {
  if (clockUrl === "") {
    return {};
  }

  try {
    const response = await fetchImpl(`${clockUrl}/sessions/${room}/cameras`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      return {};
    }

    return parseDeclaredCameras(await response.json());
  } catch (cause) {
    console.warn("[live] capacités des caméras illisibles :", cause);

    return {};
  }
}

/**
 * Lit la réponse du service, sans lui faire confiance : on ne valide que ce
 * dont on se sert, et une caméra qu'on ne sait pas lire est écartée plutôt que
 * de faire perdre les autres.
 */
export function parseDeclaredCameras(payload: unknown): DeclaredCameras {
  if (!isRecord(payload) || !isRecord(payload.cameras)) {
    return {};
  }

  const out: Record<string, CameraCapabilities> = {};

  for (const [identity, value] of Object.entries(payload.cameras)) {
    const caps = parseCapabilities(value);

    if (identity !== "" && caps !== null) {
      out[identity] = caps;
    }
  }

  return out;
}

function parseCapabilities(raw: unknown): CameraCapabilities | null {
  if (!isRecord(raw)) {
    return null;
  }

  const width = finiteNumber(raw.width);
  const height = finiteNumber(raw.height);

  // Sans définition, il n'y a rien à en tirer : c'est la seule valeur dont le
  // calcul du cadrage a besoin.
  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }

  const min = finiteNumber(raw.zoom_min);
  const max = finiteNumber(raw.zoom_max);

  return {
    width,
    height,
    lens: typeof raw.lens === "string" ? raw.lens : "",
    lenses: Array.isArray(raw.lenses) ? raw.lenses.flatMap(parseLens) : [],
    zoom: min !== null && max !== null && min > 0 && max >= min ? { min, max } : null,
  };
}

function parseLens(raw: unknown): DeclaredLens[] {
  if (!isRecord(raw) || typeof raw.id !== "string" || raw.id === "") {
    return [];
  }

  return [{ id: raw.id, label: typeof raw.label === "string" ? raw.label : "" }];
}
